import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import StringListWithSource from '@components/shared/Admin/DataCollectionFields/StringListWithSource';
import MultiSelectStringListWithSource from '@components/shared/Admin/DataCollectionFields/MultiSelectStringListWithSource';
import YesNoMaybeWithSource from '@components/shared/Admin/DataCollectionFields/YesNoMaybeWithSource';
import CountWithSource from '@components/shared/Admin/DataCollectionFields/CountWithSource';
import TextWithSource from '@components/shared/Admin/DataCollectionFields/TextWithSource';

type Props = {
  buildOutLevel: string;
  ceilingType;
  coreFactorMultiTenantFloor: number;
  coreFactorSingleTenantFloor: number;
  currentFfe: string;
  currentSpaceCondition: string;
  drinkingFountainCount: number;
  electricalRoom: string;
  elevatorLocation: string;
  exposedColumns: string;
  exposureEast: string;
  exposureNorth: string;
  exposureSouth: string;
  exposureWest: string;
  exteriorSignage: string;
  floorType: string;
  gym: string;
  heatType: string;
  interiorSignage: string;
  mainPantryKitchen: string;
  mechanicalRoom: string;
  officeFronts: string;
  officeLayout: string;
  privateBathroomsShower: string;
  receptionCeilingType: string;
  receptionFloorType: string;
  serverRoom: string;
  serviceType: string;
  sharedFloorBathroomStallCount: number;
  usableSquareFeet: number;
  viewLandmarkDetail: string;
  windowSidesCount: number;
  windowViewEast: string;
  windowViewNorth: string;
  windowViewSouth: string;
  windowViewWest: string;
  windowViews: string;
};

const ListingDataCollectionList = ({ ...props }: Props) => {
  const { t } = useTranslation('admin');
  const flags = useFlags();

  const deprecatedDataCollectionFields = [
    {
      label: t(`listing.buildOutLevel.label`),
      render: (
        <StringListWithSource
          key="buildOutLevel"
          pageName="listing"
          fieldName="buildOutLevel"
          value={props.buildOutLevel}
          sortOptionsByIndex
          options={[
            { name: 'low', value: 'Low' },
            { name: 'medium', value: 'Medium' },
            { name: 'high', value: 'High' },
          ]}
        />
      ),
    },
    {
      label: t(`listing.drinkingFountainCount`),
      render: (
        <CountWithSource
          fieldName="drinkingFountainCount"
          key="drinkingFountainCount"
          pageName="listing"
          value={props.drinkingFountainCount}
        />
      ),
    },
    {
      label: t(`listing.electricalRoom`),
      render: (
        <YesNoMaybeWithSource
          fieldName="electricalRoom"
          key="electricalRoom"
          pageName="listing"
          value={props.electricalRoom}
        ></YesNoMaybeWithSource>
      ),
    },
    {
      label: t('listing.exteriorSignage'),
      render: (
        <YesNoMaybeWithSource
          fieldName="exteriorSignage"
          key="exteriorSignage"
          pageName="listing"
          value={props.exteriorSignage}
        ></YesNoMaybeWithSource>
      ),
    },
    {
      label: t('listing.interiorSignage'),
      render: (
        <YesNoMaybeWithSource
          fieldName="interiorSignage"
          key="interiorSignage"
          pageName="listing"
          value={props.interiorSignage}
        ></YesNoMaybeWithSource>
      ),
    },
    {
      label: t('listing.mechanicalRoom'),
      render: (
        <YesNoMaybeWithSource
          fieldName="mechanicalRoom"
          key="mechanicalRoom"
          pageName="listing"
          value={props.mechanicalRoom}
        ></YesNoMaybeWithSource>
      ),
    },
    {
      label: t('listing.sharedFloorBathroomStallCount'),
      render: (
        <CountWithSource
          fieldName="sharedFloorBathroomStallCount"
          key="sharedFloorBathroomStallCount"
          pageName="listing"
          value={props.sharedFloorBathroomStallCount}
        />
      ),
    },
    {
      label: t('listing.currentFfe'),
      render: (
        <YesNoMaybeWithSource
          key="currentFfe"
          fieldName="currentFfe"
          pageName="listing"
          value={props.currentFfe}
        />
      ),
    },
    {
      label: t(`listing.elevatorLocation.label`),
      render: (
        <StringListWithSource
          key="elevatorLocation"
          pageName="listing"
          fieldName="elevatorLocation"
          value={props.elevatorLocation}
          options={[
            { name: 'side', value: 'Side' },
            { name: 'core', value: 'Core' },
          ]}
        />
      ),
    },
    {
      label: t(`listing.windowViewNorth.label`),
      render: (
        <StringListWithSource
          key="windowViewNorth"
          pageName="listing"
          fieldName="windowViewNorth"
          value={props.windowViewNorth}
          options={[
            { name: 'riverView' },
            { name: 'cityStreet' },
            { name: 'park' },
            { name: 'building' },
            { name: 'citySkyline' },
          ]}
        />
      ),
    },
    {
      label: t(`listing.windowViewEast.label`),
      render: (
        <StringListWithSource
          key="windowViewEast"
          pageName="listing"
          fieldName="windowViewEast"
          value={props.windowViewEast}
          options={[
            { name: 'riverView' },
            { name: 'cityStreet' },
            { name: 'park' },
            { name: 'building' },
            { name: 'citySkyline' },
          ]}
        />
      ),
    },
    {
      label: t(`listing.windowViewSouth.label`),
      render: (
        <StringListWithSource
          key="windowViewSouth"
          pageName="listing"
          fieldName="windowViewSouth"
          value={props.windowViewSouth}
          options={[
            { name: 'riverView' },
            { name: 'cityStreet' },
            { name: 'park' },
            { name: 'building' },
            { name: 'citySkyline' },
          ]}
        />
      ),
    },
    {
      label: t(`listing.windowViewWest.label`),
      render: (
        <StringListWithSource
          key="windowViewWest"
          pageName="listing"
          fieldName="windowViewWest"
          value={props.windowViewWest}
          options={[
            { name: 'riverView' },
            { name: 'cityStreet' },
            { name: 'park' },
            { name: 'building' },
            { name: 'citySkyline' },
          ]}
        />
      ),
    },
    {
      label: t(`listing.coreFactorMultiTenantFloor`),
      render: (
        <CountWithSource
          fieldName="coreFactorMultiTenantFloor"
          key="coreFactorMultiTenantFloor"
          pageName="listing"
          value={props.coreFactorMultiTenantFloor}
        />
      ),
    },
    {
      label: t(`listing.coreFactorSingleTenantFloor`),
      render: (
        <CountWithSource
          fieldName="coreFactorSingleTenantFloor"
          key="coreFactorSingleTenantFloor"
          pageName="listing"
          value={props.coreFactorSingleTenantFloor}
        />
      ),
    },
    {
      label: t(`listing.usableSquareFeet`),
      render: (
        <CountWithSource
          fieldName="usableSquareFeet"
          key="usableSquareFeet"
          pageName="listing"
          value={props.usableSquareFeet}
        />
      ),
    },
    {
      label: t(`listing.heatType.label`),
      render: (
        <StringListWithSource
          key="heatType"
          pageName="listing"
          fieldName="heatType"
          value={props.heatType}
          options={[
            { name: 'radiantFlooring' },
            { name: 'exposedRadiators' },
            { name: 'electric' },
            { name: 'forcedAir' },
            { name: 'hydronicPiping' },
          ]}
        />
      ),
    },
    {
      label: t(`listing.serverRoom.label`),
      render: (
        <StringListWithSource
          key="serverRoom"
          pageName="listing"
          fieldName="serverRoom"
          value={props.serverRoom}
          sortOptionsByIndex
          options={[{ name: 'yes' }, { name: 'no' }, { name: 'includedWithElectricalRoom' }]}
        />
      ),
    },
    {
      label: t(`listing.serviceType.label`),
      render: (
        <StringListWithSource
          key="serviceType"
          pageName="listing"
          fieldName="serviceType"
          value={props.serviceType}
          sortOptionsByIndex
          options={[
            { name: 'fullServiceGross' },
            { name: 'industrialGross' },
            { name: 'modifiedGross' },
            { name: 'netOfUtilities' },
            { name: 'N' },
            { name: 'NN' },
            { name: 'NNN' },
            { name: 'other' },
          ]}
        />
      ),
    },
  ];

  const amenities = [
    {
      label: t('listing.windowSidesCount'),
      render: (
        <CountWithSource
          fieldName="windowSidesCount"
          key="windowSidesCount"
          pageName="listing"
          value={props.windowSidesCount}
        />
      ),
    },
    {
      label: t(`listing.ceilingType.label`),
      render: (
        <StringListWithSource
          key="ceilingType"
          pageName="listing"
          fieldName="ceilingType"
          value={props.ceilingType}
          options={[
            { name: 'exposed' },
            { name: 'drop' },
            { name: 'sheetRock' },
            { name: 'exposedDrop' },
            { name: 'exposedSheetRock' },
            { name: 'dropSheetRock' },
          ]}
        />
      ),
    },
    {
      label: t(`listing.currentSpaceCondition.label`),
      render: (
        <StringListWithSource
          key="currentSpaceCondition"
          pageName="listing"
          fieldName="currentSpaceCondition"
          value={props.currentSpaceCondition}
          options={[
            { name: '2nd generation' },
            { name: 'category a+' },
            { name: 'challenged' },
            { name: 'core and shell' },
            { name: 'floor and shell' },
            { name: 'pre-built' },
            { name: 'pre-existing' },
            { name: 'raw' },
            { name: 'turnkey' },
            { name: 'under construction' },
            { name: 'white box' },
          ]}
        />
      ),
    },
    {
      label: t('listing.exposedColumns'),
      render: (
        <YesNoMaybeWithSource
          fieldName="exposedColumns"
          key="exposedColumns"
          pageName="listing"
          value={props.exposedColumns}
        />
      ),
    },
    {
      label: t('listing.exposureEast'),
      render: (
        <YesNoMaybeWithSource
          fieldName="exposureEast"
          key="exposureEast"
          pageName="listing"
          value={props.exposureEast}
        />
      ),
    },
    {
      label: t('listing.exposureNorth'),
      render: (
        <YesNoMaybeWithSource
          fieldName="exposureNorth"
          key="exposureNorth"
          pageName="listing"
          value={props.exposureNorth}
        />
      ),
    },
    {
      label: t('listing.exposureSouth'),
      render: (
        <YesNoMaybeWithSource
          fieldName="exposureSouth"
          key="exposureSouth"
          pageName="listing"
          value={props.exposureSouth}
        />
      ),
    },
    {
      label: t('listing.exposureWest'),
      render: (
        <YesNoMaybeWithSource
          fieldName="exposureWest"
          key="exposureWest"
          pageName="listing"
          value={props.exposureWest}
        />
      ),
    },
    {
      label: t(`listing.floorType.label`),
      render: (
        <StringListWithSource
          key="floorType"
          pageName="listing"
          fieldName="floorType"
          value={props.floorType}
          options={[
            { name: 'carpet' },
            { name: 'concrete' },
            { name: 'polishedConcrete' },
            { name: 'hardwood' },
            { name: 'marble' },
            { name: 'tiled' },
            { name: 'carpetConcrete' },
            { name: 'carpetPolishedConcrete' },
            { name: 'carpetHardwood' },
            { name: 'carpetMarble' },
            { name: 'concreteHardwood' },
            { name: 'polishedConcreteHardwood' },
            { name: 'polishedConcreteMarble' },
            { name: 'hardwoodMarble' },
          ]}
        />
      ),
    },
    {
      label: t('listing.gym'),
      render: (
        <YesNoMaybeWithSource fieldName="gym" key="gym" pageName="listing" value={props.gym} />
      ),
    },
    {
      label: t('listing.mainPantryKitchen.label'),
      render: (
        <StringListWithSource
          key="mainPantryKitchen"
          pageName="listing"
          fieldName="mainPantryKitchen"
          value={props.mainPantryKitchen}
          sortOptionsByIndex
          options={[{ name: 'open' }, { name: 'closed' }]}
        />
      ),
    },
    {
      label: t(`listing.officeFronts.label`),
      render: (
        <StringListWithSource
          key="officeFronts"
          pageName="listing"
          fieldName="officeFronts"
          value={props.officeFronts}
          options={[
            { name: 'glassWallGlassDoor' },
            { name: 'glassWallWoodDoor' },
            { name: 'glassWallMetalDoor' },
            { name: 'woodWallWoodDoor' },
            { name: 'woodWallGlassDoor' },
          ]}
        />
      ),
    },
    {
      label: t(`listing.officeLayout.label`),
      render: (
        <StringListWithSource
          key="officeLayout"
          pageName="listing"
          fieldName="officeLayout"
          value={props.officeLayout}
          options={[
            { name: 'mixOfficeOpen' },
            { name: 'openIntensive' },
            { name: 'open' },
            { name: 'perimeterOfficesWithOpen' },
            { name: 'perimeterOfficesWithSupport' },
            { name: 'interiorOfficesOpen' },
          ]}
        />
      ),
    },
    {
      label: t('listing.privateBathroomsShower'),
      render: (
        <YesNoMaybeWithSource
          fieldName="privateBathroomsShower"
          key="privateBathroomsShower"
          pageName="listing"
          value={props.privateBathroomsShower}
        />
      ),
    },

    {
      label: t(`listing.receptionCeilingType.label`),
      render: (
        <StringListWithSource
          key="receptionCeilingType"
          pageName="listing"
          fieldName="receptionCeilingType"
          value={props.receptionCeilingType}
          options={[
            { name: 'exposed' },
            { name: 'drop' },
            { name: 'sheetRock' },
            { name: 'exposedDrop' },
            { name: 'exposedSheetRock' },
            { name: 'dropSheetRock' },
          ]}
        />
      ),
    },
    {
      label: t(`listing.receptionFloorType.label`),
      render: (
        <StringListWithSource
          key="receptionFloorType"
          pageName="listing"
          fieldName="receptionFloorType"
          value={props.receptionFloorType}
          options={[
            { name: 'carpet' },
            { name: 'concrete' },
            { name: 'polishedConcrete' },
            { name: 'hardwood' },
            { name: 'marble' },
            { name: 'tiled' },
            { name: 'carpetConcrete' },
            { name: 'carpetPolishedConcrete' },
            { name: 'carpetHardwood' },
            { name: 'carpetMarble' },
            { name: 'concreteHardwood' },
            { name: 'polishedConcreteHardwood' },
            { name: 'polishedConcreteMarble' },
            { name: 'hardwoodMarble' },
          ]}
        />
      ),
    },
    {
      label: t(`listing.viewLandmarkDetail`),
      render: (
        <TextWithSource
          key="viewLandmarkDetail"
          fieldName="viewLandmarkDetail"
          pageName="listing"
          value={props.viewLandmarkDetail}
        />
      ),
    },
  ];

  // TODO: When FF 'modify-data-collection-fields' is removed, move these fields to
  // the list of amenities.
  const newDataCollectionFields = [
    {
      label: t(`listing.windowViews.label`),
      render: (
        <MultiSelectStringListWithSource
          key="windowViews"
          pageName="listing"
          fieldName="windowViews"
          values={props.windowViews}
          options={[
            { name: 'riverView' },
            { name: 'cityStreet' },
            { name: 'park' },
            { name: 'building' },
            { name: 'citySkyline' },
          ]}
        />
      ),
    },
  ];

  const orderedDataCollectionItems = (
    flags['modify-data-collection-fields']
      ? amenities.concat(newDataCollectionFields)
      : amenities.concat(deprecatedDataCollectionFields)
  )
    .sort((amenityA, amenityB) => amenityA.label.localeCompare(amenityB.label))
    .map(dataCollectionItem => {
      return dataCollectionItem.render;
    });

  return <>{orderedDataCollectionItems}</>;
};

export default memo(ListingDataCollectionList);
